<script setup lang="ts">
import { OrButtonV3, OrIconButtonV3, OrInputV3, useResponsive } from '@onereach/ui-components';
import { onMounted, ref, watch } from 'vue';

import OrLogo from '@/components/OrLogo/OrLogo.vue';
import useI18n from '@/composables/useI18n';


const props = defineProps<{
  username: string;
  isLoading: boolean;
  error: string;
  verificationCode: string | undefined;
}>();

const emit = defineEmits<{
  (e: 'toogleOpenRecovery2fa'): void;
  (e: 'logout'): void;
  (e: 'pasteFromBuffer'): void;
  (e: 'submit'): void;
  (e: 'update:verificationCode', value: string | undefined): void;
}>();

const { translate } = useI18n();
const { isDesktop } = useResponsive();

const inputError = ref('');

const submit = () => {
  inputError.value = !props.verificationCode ? translate('login.password_restore.This field is required') : '';
  if (inputError.value) return;

  emit('submit');
};

watch(() => props.verificationCode, (value) => {
  if (value?.length === 6) {
    submit();
  }
});

const twoFactorInput = ref<InstanceType<typeof OrInputV3> | null>(null!);

onMounted(() => {
  twoFactorInput.value?.focus();
});
</script>

<template>
  <div
    class="form layout-column p-md sm:p-0 sm:w-[343px] md:w-[352px]"
  >
    <OrLogo
      class="self-center md:hidden mb-md"
    />
    <div class="typography-display-small mb-md text-center md:text-left">
      {{ translate('login.login.twofactorVerification') }}
    </div>
    <span class="typography-body-2-regular mb-xl text-center md:text-left">
      {{ translate('login.login.Your profile') }} <span class="typography-body-2-bold">{{ username }}</span> {{ translate('login.login.has been configured to use two-factor authentication') }}
    </span>
    <div class="layout-row gap-md items-start">
      <OrInputV3
        ref="twoFactorInput"
        :model-value="verificationCode"
        class="verification-code flex-auto mb-xl"
        :placeholder="translate('login.login.Enter Verification Code')"
        :loading="isLoading"
        :label="translate('login.login.verificationCode')"
        :error="error || inputError"
        inputmode="decimal"
        autocomplete="one-time-code"
        @update:model-value="emit('update:verificationCode', $event)"
        @keyup.enter.native="submit"
      >
        <template
          v-if="!isLoading"
          #appendInner
        >
          <OrIconButtonV3
            :tooltip="{content: $t('login.login.Paste from clipboard'), placement: 'top'}"
            size="l"
            icon="content_paste"
            @click.stop="emit('pasteFromBuffer')"
          />
        </template>
      </OrInputV3>
    </div>

    <OrButtonV3
      v-if="isDesktop"
      class="sign-in md:self-start mb-xl"
      variant="link"
      @click="emit('toogleOpenRecovery2fa')"
    >
      {{ translate('login.login.havingProblem') }}
    </OrButtonV3>
    <slot name="captcha" />
    <OrButtonV3
      v-if="!isDesktop"
      class="sign-in !p-0 mb-xl"
      variant="text"
      @click="emit('toogleOpenRecovery2fa')"
    >
      {{ translate('login.login.havingProblem') }}
    </OrButtonV3>
    <OrButtonV3
      class="md:self-start"
      variant="link"
      @click="emit('logout')"
    >
      {{ translate('login.login.Use different email') }}
    </OrButtonV3>
  </div>
</template>
